<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { tick } from 'svelte';
  import Modal from './Modal.svelte';
  import { companiesApi, peopleApi, dealsApi, issuesApi } from '$lib/api/crm';
  import { leadsApi } from '$lib/api/leads';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';

  let { onClose }: { onClose: () => void } = $props();
  type RecordTab = 'companies' | 'people' | 'deals' | 'leads' | 'issues';
  type Result = { tab: RecordTab; id: number; label: string };
  let term = $state('');
  let debounced = $state('');
  let selected = $state(0);
  let input: HTMLInputElement;
  const verticalId = $derived(session.selectedVertical === 'all' ? undefined : session.selectedVertical);
  const issuesOnly = $derived(!!session.me?.user.issues_only);
  $effect(() => { const value = term; const timer = setTimeout(() => { debounced = value.trim(); selected = 0; }, 250); return () => clearTimeout(timer); });
  $effect(() => { if (input) tick().then(() => input.focus()); });
  const query = createQuery(() => ({
    queryKey: ['command-search', debounced, verticalId, issuesOnly],
    enabled: !!debounced,
    queryFn: async () => {
      const params = { q: debounced, per_page: 5, vertical_id: verticalId };
      const sources: { tab: RecordTab; run: () => Promise<Result[]> }[] = [
        { tab: 'issues', run: async () => (await issuesApi.list(params)).results.map(r => ({ tab: 'issues', id: r.id, label: r.title })) }
      ];
      if (!issuesOnly) sources.unshift(
        { tab: 'companies', run: async () => (await companiesApi.list(params)).results.map(r => ({ tab: 'companies', id: r.id, label: r.name })) },
        { tab: 'people', run: async () => (await peopleApi.list(params)).results.map(r => ({ tab: 'people', id: r.id, label: `${r.first_name} ${r.last_name}`.trim() || r.email || `Contact #${r.id}` })) },
        { tab: 'deals', run: async () => (await dealsApi.list(params)).results.map(r => ({ tab: 'deals', id: r.id, label: r.name })) },
        { tab: 'leads', run: async () => (await leadsApi.list(params)).results.map(r => ({ tab: 'leads', id: r.id, label: r.name || r.email || `Lead #${r.id}` })) }
      );
      const settled = await Promise.allSettled(sources.map(source => source.run()));
      return {
        rows: settled.flatMap(result => result.status === 'fulfilled' ? result.value : []),
        errors: settled.flatMap((result, i) => result.status === 'rejected' ? [`${sources[i].tab}: ${getErrorMessage(result.reason)}`] : [])
      };
    }
  }));
  const rows = $derived(query.data?.rows ?? []);
  const current = $derived(rows[selected]);
  function pick(row: Result) { route.push({ tab: row.tab, id: row.id }); onClose(); }
  function create() {
    if (!current) return;
    const tab = current.tab;
    route.push({ tab }, () => session.requestCreate(tab));
    onClose();
  }
  function keydown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      selected = rows.length ? (selected + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length : 0;
      document.getElementById(`jump-result-${selected}`)?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && current) { e.preventDefault(); pick(current); }
  }
</script>

<Modal title="Jump to a record · ⌘/Ctrl K" {onClose} wide>
  <label for="command-search" class="label">Search {issuesOnly ? 'issues' : 'companies, contacts, deals, leads and issues'}</label>
  <input bind:this={input} id="command-search" class="input" type="search" bind:value={term} onkeydown={keydown}
    role="combobox" aria-autocomplete="list" aria-expanded={rows.length > 0} aria-controls="jump-results" aria-activedescendant={current ? `jump-result-${selected}` : undefined} />
  <p class="my-2 text-xs text-dark-gray">↑ ↓ to select · Enter to open · Esc to close</p>
  {#if query.isFetching}<p role="status" class="text-sm">Searching…</p>{/if}
  {#if query.data?.errors.length}<p role="alert" class="text-sm text-red-700">{query.data.errors.join(' · ')}</p><button type="button" class="btn-secondary" onclick={() => query.refetch()}>Retry unavailable searches</button>{/if}
  <div id="jump-results" role="listbox" aria-label="Matching records" class="max-h-[45vh] overflow-auto">
    {#each rows as row, index (`${row.tab}-${row.id}`)}
      <button id={`jump-result-${index}`} role="option" aria-selected={selected === index} tabindex="-1" type="button" class="mb-1 flex w-full items-center justify-between gap-3 rounded-lg p-3 text-left text-sm {selected === index ? 'bg-navy text-white' : 'bg-gray-50 text-navy'}" onclick={() => pick(row)}>
        <span>{row.label}</span><span class="text-xs capitalize">{row.tab}</span>
      </button>
    {:else}
      <p class="py-5 text-sm text-dark-gray">{debounced ? 'No matching records. Try a name or reference.' : 'Type to search your accessible records.'}</p>
    {/each}
  </div>
  {#if current}<div class="mt-3 flex gap-2"><button type="button" class="btn-primary" onclick={() => pick(current)}>Open selected record</button><button type="button" class="btn-secondary" onclick={create}>New in {current.tab}</button></div>{/if}
</Modal>
